import React, { useState, useEffect, useContext, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import LoadingContext from '../../context/Loading/Loading'

import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import InputGroup from 'react-bootstrap/InputGroup'
import Alert from 'react-bootstrap/Alert'

import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'
import {
  getFnbItem,
  createFnbItem,
  updateFnbItem,
  getFnbCategories,
  getFnbLocations,
  getOrgVenues,
  ticketExchangeGetEvents,
  upload,
} from '../../utilities/api'
import { b64toBlob } from '../../utilities/helpers'

import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { UploadImage } from '../UploadImage'
import { TrashButton } from '../TrashButton'

import '../MarketplaceWrapper/marketplace.scss'

const EMPTY_FORM = {
  name: '',
  shortName: '',
  sku: '',
  categoryId: '',
  venueUuid: '',
  description: '',
  isActive: true,
  price: '',
  cost: '',
  isTaxable: false,
  taxRate: '',
  availableAtAllLocations: true,
  locationIds: [],
  availabilityScope: 'all_events',
  availabilityEventUuids: [],
  availabilityVenueUuids: [],
  availabilityEventTypes: '',
}

function buildAvailabilityPayload(form) {
  const scope = form.availabilityScope || 'all_events'

  if (scope === 'specific_events') {
    return {
      scope,
      eventUuids: form.availabilityEventUuids.map(String),
    }
  }

  if (scope === 'venues') {
    return {
      scope,
      venueUuids: form.availabilityVenueUuids.map(String),
    }
  }

  if (scope === 'event_types') {
    const eventTypes = String(form.availabilityEventTypes || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
    return {
      scope,
      eventTypes,
    }
  }

  return { scope: 'all_events' }
}

function getRowAttrs(row) {
  return row.attributes ?? row
}

function hasMarketplacePermission(getPermissions, orgPermissionsReady) {
  if (!orgPermissionsReady) return false
  const perms = getPermissions()
  return (
    checkPermission(perms, ORGANIZATION_PERMISSION_SLUGS.MANAGE_MARKETPLACE) ||
    checkPermission(perms, ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT)
  )
}

function calcMargin(price, cost) {
  const p = Number(price ?? 0)
  const c = Number(cost ?? 0)
  if (p <= 0) return 0
  return Math.round(((p - c) / p) * 100)
}

function formatCurrency(value) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value ?? 0))
}

function getFnbImageUrl(attrs) {
  const media = attrs?.imageUrl
  if (!media) return ''
  if (typeof media === 'string') return media
  return media?.data?.attributes?.url ?? media?.url ?? ''
}

function resolveMediaUrl(url) {
  if (!url) return ''
  if (url.startsWith('http')) return url
  if (url.startsWith('/')) {
    const base = process.env.REACT_APP_API?.replace(/\/api\/?$/, '') || ''
    return `${base}${url}`
  }
  return url
}

function isExistingImageUrl(src) {
  if (!src) return false
  return (
    src.startsWith('https://') ||
    src.startsWith('http://') ||
    src.startsWith('/')
  )
}

async function uploadImageIfNeeded(imageData) {
  if (!imageData || isExistingImageUrl(imageData)) return undefined

  const block = imageData.split(';')
  const contentType = block[0].split(':')[1]
  const realData = block[1].split(',')[1]
  const formData = new FormData()
  formData.append('files', b64toBlob(realData, contentType))
  const res = await upload(formData)
  return res?.data?.[0]?.id ?? null
}

export default function MarketplaceItemFormWrapper() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = Boolean(id)

  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgUUID = AuthService.getOrg()[0]?.uuid
  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission = hasMarketplacePermission(
    getPermissions,
    orgPermissionsReady
  )

  const [form, setForm] = useState(EMPTY_FORM)
  const [categories, setCategories] = useState([])
  const [locations, setLocations] = useState([])
  const [venues, setVenues] = useState([])
  const [orgEvents, setOrgEvents] = useState([])
  const [isLoadingEvents, setIsLoadingEvents] = useState(false)
  const [isLoadingVenues, setIsLoadingVenues] = useState(true)
  const [image, setImage] = useState('')
  const [initialImage, setInitialImage] = useState('')
  const [coordinates, setCoordinates] = useState()
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const requiresVenueSelection = venues.length > 1
  const defaultVenueUuid = venues.length === 1 ? (venues[0]?.uuid ?? '') : ''

  const visibleCategories = useMemo(() => {
    const venueUuid = form.venueUuid || defaultVenueUuid
    return categories.filter((row) => {
      const a = getRowAttrs(row)
      if (!venueUuid) return true
      return !a.venue_uuid || a.venue_uuid === venueUuid
    })
  }, [categories, form.venueUuid, defaultVenueUuid])

  const visibleLocations = useMemo(() => {
    const venueUuid = form.venueUuid || defaultVenueUuid
    if (!venueUuid) return locations
    return locations.filter((row) => {
      const a = getRowAttrs(row)
      return !a.venue_uuid || a.venue_uuid === venueUuid
    })
  }, [locations, form.venueUuid, defaultVenueUuid])

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    if (!orgUUID) return
    showLoading()
    setIsLoadingData(true)

    const requests = [
      getFnbCategories(orgUUID),
      getFnbLocations(orgUUID),
      getOrgVenues(orgUUID),
    ]
    if (isEditing) {
      requests.push(getFnbItem(id))
    }

    Promise.all(requests)
      .then((results) => {
        const categoriesRes = results[0]
        const locationsRes = results[1]
        const venuesRes = results[2]
        setCategories(categoriesRes?.data?.data ?? [])
        setLocations(locationsRes?.data?.data ?? [])
        setVenues(Array.isArray(venuesRes?.data) ? venuesRes.data : [])

        if (isEditing && results[3]) {
          const row = results[3]?.data?.data
          const a = getRowAttrs(row)
          const availability = a.availability ?? {}
          setForm({
            name: a.name ?? '',
            shortName: a.shortName ?? '',
            sku: a.sku ?? '',
            categoryId: a.categoryId ?? '',
            venueUuid: a.venue_uuid ?? '',
            description: a.description ?? '',
            isActive: a.isActive !== false,
            price: a.price != null ? String(a.price) : '',
            cost: a.cost != null ? String(a.cost) : '',
            isTaxable: a.isTaxable ?? false,
            taxRate: a.taxRate != null ? String(a.taxRate) : '',
            availableAtAllLocations: a.availableAtAllLocations !== false,
            locationIds: Array.isArray(a.locationIds)
              ? a.locationIds.map(String)
              : [],
            availabilityScope: availability.scope ?? 'all_events',
            availabilityEventUuids: Array.isArray(availability.eventUuids)
              ? availability.eventUuids.map(String)
              : [],
            availabilityVenueUuids: Array.isArray(availability.venueUuids)
              ? availability.venueUuids.map(String)
              : [],
            availabilityEventTypes: Array.isArray(availability.eventTypes)
              ? availability.eventTypes.join(', ')
              : '',
          })
          const loadedImage = resolveMediaUrl(getFnbImageUrl(a))
          setImage(loadedImage)
          setInitialImage(loadedImage)
        }
      })
      .catch((err) => {
        console.error(err)
        setError('Failed to load item.')
      })
      .finally(() => {
        setIsLoadingData(false)
        setIsLoadingVenues(false)
        hideLoading()
      })
  }, [orgPermissionsReady, hasPermission, orgUUID, id, isEditing])

  useEffect(() => {
    if (!orgUUID || form.availabilityScope !== 'specific_events') return

    setIsLoadingEvents(true)
    ticketExchangeGetEvents(orgUUID)
      .then((res) => {
        setOrgEvents(Array.isArray(res?.data) ? res.data : [])
      })
      .catch((err) => {
        console.error(err)
        setOrgEvents([])
      })
      .finally(() => setIsLoadingEvents(false))
  }, [orgUUID, form.availabilityScope])

  const margin = useMemo(
    () => calcMargin(form.price, form.cost),
    [form.price, form.cost]
  )

  const profitPerItem = useMemo(() => {
    const p = Number(form.price ?? 0)
    const c = Number(form.cost ?? 0)
    return p - c
  }, [form.price, form.cost])

  const canSubmit = form.name.trim().length >= 2

  const toggleLocation = (locationId) => {
    const idStr = String(locationId)
    setForm((f) => {
      const ids = f.locationIds.includes(idStr)
        ? f.locationIds.filter((x) => x !== idStr)
        : [...f.locationIds, idStr]
      return { ...f, locationIds: ids, availableAtAllLocations: false }
    })
  }

  const handleAllLocationsChange = (checked) => {
    setForm((f) => ({
      ...f,
      availableAtAllLocations: checked,
      locationIds: checked ? [] : f.locationIds,
    }))
  }

  const toggleAvailabilityEvent = (eventUuid) => {
    const uuid = String(eventUuid)
    setForm((current) => {
      const selected = current.availabilityEventUuids.includes(uuid)
        ? current.availabilityEventUuids.filter((entry) => entry !== uuid)
        : [...current.availabilityEventUuids, uuid]
      return { ...current, availabilityEventUuids: selected }
    })
  }

  const toggleAvailabilityVenue = (venueUuid) => {
    const uuid = String(venueUuid)
    setForm((current) => {
      const selected = current.availabilityVenueUuids.includes(uuid)
        ? current.availabilityVenueUuids.filter((entry) => entry !== uuid)
        : [...current.availabilityVenueUuids, uuid]
      return { ...current, availabilityVenueUuids: selected }
    })
  }

  const handleVenueChange = (venueUuid) => {
    setForm((current) => {
      const selectedCategory = categories.find(
        (row) => String(row.id) === String(current.categoryId)
      )
      const categoryAttrs = selectedCategory
        ? getRowAttrs(selectedCategory)
        : null
      const categoryVenueMismatch =
        categoryAttrs?.venue_uuid &&
        venueUuid &&
        categoryAttrs.venue_uuid !== venueUuid

      const nextLocationIds = current.locationIds.filter((locationId) => {
        const location = locations.find(
          (row) => String(row.id) === String(locationId)
        )
        const locationAttrs = location ? getRowAttrs(location) : null
        if (!venueUuid) return true
        return (
          !locationAttrs?.venue_uuid || locationAttrs.venue_uuid === venueUuid
        )
      })

      return {
        ...current,
        venueUuid,
        categoryId: categoryVenueMismatch ? '' : current.categoryId,
        locationIds: nextLocationIds,
      }
    })
  }

  const handleRemoveImage = () => {
    setImage('')
    setCoordinates()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!orgUUID || !canSubmit) return
    setSaving(true)
    setError('')

    try {
      let imageUrlId
      if (image !== initialImage) {
        if (!image) {
          imageUrlId = null
        } else if (isExistingImageUrl(image)) {
          imageUrlId = undefined
        } else {
          imageUrlId = await uploadImageIfNeeded(image)
        }
      }

      const venueUuid = form.venueUuid || defaultVenueUuid
      const payload = {
        organizationId: orgUUID,
        venue_uuid: venueUuid || null,
        name: form.name.trim(),
        shortName: form.shortName.trim(),
        sku: form.sku.trim(),
        categoryId: form.categoryId || null,
        description: form.description.trim(),
        isActive: form.isActive,
        price: Number(form.price) || 0,
        cost: Number(form.cost) || 0,
        isTaxable: form.isTaxable,
        taxRate: Number(form.taxRate) || 0,
        availableAtAllLocations: form.availableAtAllLocations,
        locationIds: form.availableAtAllLocations ? [] : form.locationIds,
        availability: buildAvailabilityPayload(form),
      }

      if (imageUrlId !== undefined) {
        payload.imageUrl = imageUrlId
      }

      const request = isEditing
        ? updateFnbItem(id, payload)
        : createFnbItem(payload)

      await request
      navigate('/settings/marketplace/items')
    } catch (err) {
      console.error(err)
      setError('Failed to save item. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (!orgPermissionsReady) return <PageLoadingContainer />
  if (!hasPermission) return <NoPermissionsContainer />
  if (isLoading || isLoadingData) return <PageLoadingContainer />

  return (
    <Form className="marketplace-form" onSubmit={handleSubmit}>
      {error ? <Alert variant="danger">{error}</Alert> : null}

      <div className="marketplace-form-card">
        <h2 className="marketplace-form-card__title">Basic details</h2>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group controlId="fnb-item-name">
              <Form.Label>Item name</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Hot Dog"
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="fnb-item-short-name">
              <Form.Label>Short name</Form.Label>
              <Form.Control
                value={form.shortName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, shortName: e.target.value }))
                }
                placeholder="Hot Dog"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="fnb-item-sku">
              <Form.Label>SKU</Form.Label>
              <Form.Control
                value={form.sku}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sku: e.target.value }))
                }
                placeholder="FOOD-001"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="fnb-item-category">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={form.categoryId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, categoryId: e.target.value }))
                }
              >
                <option value="">Select category</option>
                {visibleCategories.map((row) => {
                  const a = getRowAttrs(row)
                  return (
                    <option key={row.id} value={String(row.id)}>
                      {a.name}
                    </option>
                  )
                })}
              </Form.Select>
            </Form.Group>
          </Col>
          {requiresVenueSelection ? (
            <Col md={4}>
              <Form.Group controlId="fnb-item-venue">
                <Form.Label>Venue</Form.Label>
                {isLoadingVenues ? (
                  <div className="text-muted small">Loading venues...</div>
                ) : (
                  <Form.Select
                    value={form.venueUuid}
                    onChange={(e) => handleVenueChange(e.target.value)}
                  >
                    <option value="">All venues</option>
                    {venues.map((venue) => (
                      <option key={venue.uuid} value={venue.uuid}>
                        {venue.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Form.Text className="text-muted">
                  Optional. Restrict this item to one venue.
                </Form.Text>
              </Form.Group>
            </Col>
          ) : null}
          <Col md={4}>
            <Form.Group controlId="fnb-item-status">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={form.isActive ? 'active' : 'inactive'}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    isActive: e.target.value === 'active',
                  }))
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group controlId="fnb-item-description">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="Optional item description"
              />
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group controlId="fnb-item-image">
              <Form.Label>Item image</Form.Label>
              <p className="marketplace-form-card__subtitle mb-2">
                Optional. Use a square image (e.g. 1080 × 1080px) for best
                results.
              </p>
              <div
                className={`marketplace-item-image${image ? ' marketplace-item-image--has-image' : ''}`}
              >
                <UploadImage
                  dropzoneSize="sm"
                  dropzoneWithRadius={true}
                  selectedImage={image}
                  setSelectedImage={setImage}
                  setCoordinates={setCoordinates}
                  coordinates={coordinates}
                  size="lg"
                  altText={form.name ? `Image for ${form.name}` : 'Item image'}
                />
                {image ? (
                  <TrashButton
                    onClick={handleRemoveImage}
                    isDisabled={saving}
                    variant="danger"
                  />
                ) : null}
              </div>
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="marketplace-form-card">
        <h2 className="marketplace-form-card__title">Pricing</h2>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group controlId="fnb-item-price">
              <Form.Label>Selling price</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.price}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, price: e.target.value }))
                  }
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="fnb-item-cost">
              <Form.Label>Cost</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.cost}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, cost: e.target.value }))
                  }
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="fnb-item-tax-rate">
              <Form.Label>Tax rate (%)</Form.Label>
              <Form.Control
                type="number"
                min={0}
                step="0.01"
                value={form.taxRate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, taxRate: e.target.value }))
                }
                disabled={!form.isTaxable}
              />
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Check
              type="switch"
              id="fnb-item-taxable"
              label="Taxable"
              checked={form.isTaxable}
              onChange={(e) =>
                setForm((f) => ({ ...f, isTaxable: e.target.checked }))
              }
            />
          </Col>
        </Row>
        <div className="marketplace-margin-stats">
          <div className="marketplace-margin-stats__item">
            <span className="marketplace-margin-stats__label">Margin</span>
            <span className="marketplace-margin-stats__value">{margin}%</span>
          </div>
          <div className="marketplace-margin-stats__item">
            <span className="marketplace-margin-stats__label">
              Profit per item
            </span>
            <span className="marketplace-margin-stats__value">
              {formatCurrency(profitPerItem)}
            </span>
          </div>
        </div>
      </div>

      <div className="marketplace-form-card">
        <h2 className="marketplace-form-card__title">Availability</h2>
        <p className="marketplace-form-card__subtitle">
          Choose where this item can be sold.
        </p>
        <div className="marketplace-checkbox-list mb-4">
          <Form.Check
            type="checkbox"
            id="fnb-all-locations"
            label="All locations"
            checked={form.availableAtAllLocations}
            onChange={(e) => handleAllLocationsChange(e.target.checked)}
          />
          {visibleLocations.map((row) => {
            const a = getRowAttrs(row)
            return (
              <Form.Check
                key={row.id}
                type="checkbox"
                id={`fnb-location-${row.id}`}
                label={a.name}
                checked={
                  form.availableAtAllLocations ||
                  form.locationIds.includes(String(row.id))
                }
                disabled={form.availableAtAllLocations}
                onChange={() => toggleLocation(row.id)}
              />
            )
          })}
          {visibleLocations.length === 0 && (
            <p className="text-muted mb-0">
              No locations yet. Add locations under Marketplace → Locations.
            </p>
          )}
        </div>

        <Form.Group controlId="fnb-availability-scope">
          <Form.Label>Available for</Form.Label>
          <Form.Select
            value={form.availabilityScope}
            onChange={(e) =>
              setForm((f) => ({ ...f, availabilityScope: e.target.value }))
            }
          >
            <option value="all_events">All events</option>
            <option value="specific_events">Specific events</option>
            <option value="event_types">Specific event types</option>
            <option value="venues">Specific venues</option>
          </Form.Select>
          {form.availabilityScope === 'specific_events' && (
            <div className="mt-2 marketplace-checkbox-list">
              {isLoadingEvents ? (
                <p className="text-muted mb-0">Loading events...</p>
              ) : orgEvents.length === 0 ? (
                <p className="text-muted mb-0">
                  No upcoming events found for this organization.
                </p>
              ) : (
                orgEvents.map((event) => (
                  <Form.Check
                    key={event.uuid}
                    type="checkbox"
                    id={`fnb-availability-event-${event.uuid}`}
                    label={event.name}
                    checked={form.availabilityEventUuids.includes(
                      String(event.uuid)
                    )}
                    onChange={() => toggleAvailabilityEvent(event.uuid)}
                  />
                ))
              )}
            </div>
          )}
          {form.availabilityScope === 'venues' && (
            <div className="mt-2 marketplace-checkbox-list">
              {venues.length === 0 ? (
                <p className="text-muted mb-0">No venues found.</p>
              ) : (
                venues.map((venue) => (
                  <Form.Check
                    key={venue.uuid}
                    type="checkbox"
                    id={`fnb-availability-venue-${venue.uuid}`}
                    label={venue.name}
                    checked={form.availabilityVenueUuids.includes(
                      String(venue.uuid)
                    )}
                    onChange={() => toggleAvailabilityVenue(venue.uuid)}
                  />
                ))
              )}
            </div>
          )}
          {form.availabilityScope === 'event_types' && (
            <Form.Control
              className="mt-2"
              value={form.availabilityEventTypes}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  availabilityEventTypes: e.target.value,
                }))
              }
              placeholder="concert, sports, festival"
            />
          )}
          {form.availabilityScope === 'event_types' && (
            <Form.Text className="text-muted">
              Comma-separated event type values matched against each
              event&apos;s type field.
            </Form.Text>
          )}
        </Form.Group>
      </div>

      <div className="marketplace-form-actions">
        <Button
          type="button"
          variant="outline-secondary"
          onClick={() => navigate('/settings/marketplace/items')}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={saving || !canSubmit}>
          {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add item'}
        </Button>
      </div>
    </Form>
  )
}
